import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "menu", "modal", "nameInput", "presetGroupInput", "presetNameInput", "savePresetInput"]
  static values = {
    open: Boolean,
    presetGroup: String,
    filterValues: Object,
    filterUrl: String
  }

  connect() {
    this.openValue = false
    // Listen for other dropdowns opening
    this.closeHandler = this.closeFromOtherDropdown.bind(this)
    window.addEventListener("dropdown:open", this.closeHandler)
  }

  disconnect() {
    window.removeEventListener("dropdown:open", this.closeHandler)
  }

  toggle(event) {
    this.openValue = !this.openValue
    event.preventDefault()
  }

  openValueChanged() {
    if (this.openValue) {
      // Notify other dropdowns to close
      window.dispatchEvent(new CustomEvent("dropdown:open", { detail: { source: this.element } }))
      this.menuTarget.classList.remove("hidden")
      this.buttonTarget.setAttribute("aria-expanded", "true")
    } else {
      this.menuTarget.classList.add("hidden")
      this.buttonTarget.setAttribute("aria-expanded", "false")
    }
  }

  closeFromOtherDropdown(event) {
    // Close this dropdown if another dropdown opened
    if (event.detail.source !== this.element) {
      this.openValue = false
    }
  }

  openSaveModal(event) {
    event.preventDefault()
    event.stopPropagation()
    this.openValue = false

    // Open the modal
    this.modalTarget.showModal()
  }

  closeModal(event) {
    event.preventDefault()
    this.modalTarget.close()
  }

  savePreset(event) {
    event.preventDefault()

    // Get the name from the modal input
    const name = this.nameInputTarget.value.trim()
    if (!name) {
      alert("Please enter a preset name")
      return
    }

    // Get the filter form
    const form = this.element

    // Store original action and method
    const originalAction = form.action
    const originalMethod = form.method

    // Update form to POST to filter_presets
    form.action = "/filter_presets"
    form.method = "post"

    // Populate hidden fields
    this.presetNameInputTarget.value = name
    this.savePresetInputTarget.value = "1"

    // Close modal
    this.modalTarget.close()

    // Submit the form
    form.requestSubmit()

    // Reset form action/method (though page will reload anyway)
    setTimeout(() => {
      form.action = originalAction
      form.method = originalMethod
      this.presetNameInputTarget.value = ""
      this.savePresetInputTarget.value = ""
    }, 0)
  }

  updatePreset(event) {
    event.preventDefault()
    event.stopPropagation()
    this.openValue = false

    // Get preset ID from the button's data attribute
    const presetId = event.currentTarget.dataset.presetId

    if (!presetId) {
      alert("No preset selected")
      return
    }

    // Get the filter form
    const form = this.element

    // Store original action and method
    const originalAction = form.action
    const originalMethod = form.method

    // Update form to PATCH to filter_presets/:id
    form.action = `/filter_presets/${presetId}`
    form.method = "post"

    // Add method override for PATCH
    let methodInput = form.querySelector('input[name="_method"]')
    if (!methodInput) {
      methodInput = document.createElement("input")
      methodInput.type = "hidden"
      methodInput.name = "_method"
      form.appendChild(methodInput)
    }
    methodInput.value = "patch"

    // Submit the form
    form.requestSubmit()

    // Reset form action/method (though page will reload anyway)
    setTimeout(() => {
      form.action = originalAction
      form.method = originalMethod
      if (methodInput) {
        methodInput.remove()
      }
    }, 0)
  }

  deletePreset(event) {
    event.preventDefault()
    const presetId = event.currentTarget.dataset.presetId

    if (!confirm("Are you sure you want to delete this preset?")) {
      return
    }

    // Create a form to delete the preset
    const form = document.createElement("form")
    form.method = "POST"
    form.action = `/filter_presets/${presetId}`

    const methodInput = document.createElement("input")
    methodInput.type = "hidden"
    methodInput.name = "_method"
    methodInput.value = "delete"
    form.appendChild(methodInput)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.openValue = false
    }
  }
}
